import apiClient from '../axios.js';

/**
 * LINE 推播管理 API
 */
export const lineBroadcastService = {
    /**
     * 獲取推播列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.pageSize - 每頁數量
     * @param {string} params.status - 狀態，可逗號分隔多個（draft, scheduled, sending, completed, failed, cancelled）
     * @param {string} params.search - 搜尋關鍵字（可選）
     * @returns {Promise<Object>} 推播列表
     */
    getBroadcasts: async (params = {}) => {
        const response = await apiClient.get('/line/broadcast', { params });
        return response;
    },

    /**
     * 獲取單一推播詳情
     * @param {string} id - 推播 ID
     * @returns {Promise<Object>} 推播詳情
     */
    getBroadcast: async (id) => {
        const response = await apiClient.get(`/line/broadcast/${id}`);
        return response;
    },

    /**
     * 建立推播活動
     * @param {Object} broadcastData - 推播資料
     * @param {string} broadcastData.campaignName - 活動名稱
     * @param {string} broadcastData.messageType - 訊息類型（text, flex, image, etc.）
     * @param {Object} broadcastData.messageContent - 訊息內容
     * @param {Array<string>} broadcastData.lineConfigIds - LINE 官方帳號設定 ID 陣列
     * @param {string} broadcastData.audienceType - 受眾類型（tags, rule, segment, custom_rule）
     * @param {Object} broadcastData.audienceConfig - 受眾配置
     * @param {string} broadcastData.scheduledAt - 排程發送時間（可選）
     * @returns {Promise<Object>} 建立的推播
     */
    createBroadcast: async (broadcastData) => {
        const response = await apiClient.post('/line/broadcast', broadcastData);
        return response;
    },

    /**
     * 查詢推播統計
     * @param {string} id - 推播 ID
     * @returns {Promise<Object>} 推播統計
     */
    getBroadcastStatistics: async (id) => {
        const response = await apiClient.get(`/line/broadcast/${id}/statistics`);
        return response;
    },

    /**
     * 預覽推播受眾人數
     * @param {Object} previewData - 預覽資料
     * @param {string} previewData.audienceType - 受眾類型
     * @param {Object} previewData.audienceConfig - 受眾配置
     * @param {Array<string>} previewData.lineConfigIds - LINE 官方帳號設定 ID 陣列
     * @returns {Promise<Object>} 預覽結果（包含 estimatedCount）
     */
    previewAudience: async (previewData) => {
        const response = await apiClient.post('/line/broadcast/preview', previewData);
        return response;
    },

    /**
     * 更新推播活動（僅限草稿狀態）
     * @param {string} id - 推播 ID
     * @param {Object} broadcastData - 推播資料
     * @returns {Promise<Object>} 更新後的推播
     */
    updateBroadcast: async (id, broadcastData) => {
        const response = await apiClient.put(`/line/broadcast/${id}`, broadcastData);
        return response;
    },

    /**
     * 刪除推播活動（僅限草稿狀態）
     * @param {string} id - 推播 ID
     * @returns {Promise<Object>} 刪除結果
     */
    deleteBroadcast: async (id) => {
        const response = await apiClient.delete(`/line/broadcast/${id}`);
        return response;
    },

    /**
     * 從 LINE Insight API 查詢推播成效（已讀率、點擊率）
     * ⚠️ 資料有 T+1 延遲，發送當天查詢通常回傳 unready
     * @param {string} id - 推播 ID
     * @returns {Promise<Object>} { status, delivered, opened, clicked, ... }
     */
    fetchInsight: async (id) => {
        const response = await apiClient.post(`/line/broadcast/${id}/fetch-insight`);
        return response;
    },

    /**
     * 重試失敗批次（重新入 SQS，403 封鎖除外）
     * @param {string} id - 推播 ID
     * @returns {Promise<Object>} 重試結果
     */
    retryFailedBatches: async (id) => {
        const response = await apiClient.post(`/line/broadcast/${id}/retry-failed`);
        return response;
    },

    /**
     * 查詢推播批次紀錄（可依狀態過濾）
     * @param {string} id - 推播 ID
     * @param {Object} params - 過濾參數（status: 'pending' | 'sent' | 'failed'）
     * @returns {Promise<Object>} 批次列表
     */
    getBroadcastBatches: async (id, params = {}) => {
        const response = await apiClient.get(`/line/broadcast/${id}/batches`, { params });
        return response;
    },

    /**
     * 取消推播（如果尚未發送）
     * @param {string} id - 推播 ID
     * @returns {Promise<Object>} 取消結果
     */
    cancelBroadcast: async (id) => {
        const response = await apiClient.post(`/line/broadcast/${id}/cancel`);
        return response;
    }
};

/**
 * LINE 週期推播管理 API
 */
export const lineBroadcastRecurringService = {
    /** 列表 */
    list: async (params = {}) => {
        return apiClient.get('/line/broadcast/recurring', { params });
    },

    /** 建立 */
    create: async (data) => {
        return apiClient.post('/line/broadcast/recurring', data);
    },

    /** 詳情 */
    get: async (id) => {
        return apiClient.get(`/line/broadcast/recurring/${id}`);
    },

    /** 更新 */
    update: async (id, data) => {
        return apiClient.put(`/line/broadcast/recurring/${id}`, data);
    },

    /** 刪除（封存） */
    archive: async (id) => {
        return apiClient.delete(`/line/broadcast/recurring/${id}`);
    },

    /** 暫停 */
    pause: async (id) => {
        return apiClient.post(`/line/broadcast/recurring/${id}/pause`);
    },

    /** 恢復 */
    resume: async (id) => {
        return apiClient.post(`/line/broadcast/recurring/${id}/resume`);
    },

    /** 執行記錄 */
    getExecutions: async (id, params = {}) => {
        return apiClient.get(`/line/broadcast/recurring/${id}/executions`, { params });
    },

    /** 農曆節慶清單 */
    getFestivals: async () => {
        return apiClient.get('/line/broadcast/recurring/festivals');
    }
};

export default lineBroadcastService;
